import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { MODULES } from "@/config/modules";
import { RecordForm } from "@/components/DataModule";
import FlowGuide from "@/components/FlowGuide";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, TrendingUp, TrendingDown, Wallet, Percent, AlertTriangle, Zap, Repeat, FolderKanban } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const marginColor = (pct) => (pct >= 40 ? "text-emerald-600" : pct >= 20 ? "text-amber-600" : "text-red-600");

function Row({ label, val }) {
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-slate-50">
      <span className="text-slate-500">{label}</span><span className="text-slate-800 font-medium">{val}</span>
    </div>
  );
}

export default function ContractMargins() {
  const { company } = useTenant();
  const cur = company?.currency || "EUR";
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && data) {
      const c = data.contracts.find((x) => x.id === openId);
      if (c) openDetail(c);
      else toast.error("That contract no longer exists");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  const load = useCallback(() => { api.get("/contract-margins").then((r) => setData(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ contract_type: "Recurring", service_frequency: "Monthly" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/contracts/${editing.id}`, form);
      else await api.post("/contracts", form);
      toast.success(form.contract_type === "Recurring" ? "Contract saved · recurring visits generated" : "Contract saved");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const genWork = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/contracts/generate-work");
      toast.success(`Generated: ${data.recurring_works} recurring work(s) · ${data.projects} project(s) · ${data.jobs_created} job(s) into Unscheduled · ${data.skipped} skipped (already linked)`, { duration: 10000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate work"); } finally { setGenBusy(false); }
  };

  const openDetail = async (c) => {
    setLoadingDetail(true); setDetail({ contract: c }); setTrend(null);
    api.get(`/contract-margins/${c.id}/trend`).then((r) => setTrend(r.data.series)).catch(() => setTrend([]));
    try { const { data } = await api.get(`/contract-margins/${c.id}`); setDetail(data); }
    catch (e) { toast.error("Failed to load margin"); } finally { setLoadingDetail(false); }
  };

  const t = data?.totals;
  const rate = data?.labour_rate || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-head font-bold text-slate-900">Contract Margins &amp; Pipeline</h1>
          <p className="text-sm text-slate-500">Live revenue vs cost per site · labour costed at {money(rate, cur)}/hr</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="generate-work-btn" onClick={genWork} disabled={genBusy}>
            {genBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}Generate work from contracts
          </Button>
          <Button className="btn-accent" data-testid="contract-new-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" />New Contract</Button>
        </div>
      </div>

      <FlowGuide />

      {rate === 0 && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 flex items-center gap-2" data-testid="labour-rate-warning">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />No labour cost rate set — labour cost shows as {money(0, cur)}. Set it in <Link to="/app/settings" className="underline font-medium">Settings → Company &amp; Branding</Link>.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {[
          { label: "Revenue", val: money(t?.revenue || 0, cur), icon: Wallet, cls: "text-slate-900" },
          { label: "Cost", val: money(t?.cost || 0, cur), icon: TrendingDown, cls: "text-slate-900" },
          { label: "Margin", val: money(t?.margin || 0, cur), icon: TrendingUp, cls: marginColor(t?.margin_pct || 0) },
          { label: "Margin %", val: `${t?.margin_pct || 0}%`, icon: Percent, cls: marginColor(t?.margin_pct || 0) },
          { label: "Pipeline", val: money(t?.pipeline_value || 0, cur), icon: TrendingUp, cls: "accent-text" },
        ].map((c) => (
          <div key={c.label} data-testid={`margin-total-${c.label.toLowerCase().replace(/[^a-z]/g, "")}`} className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-slate-400"><c.icon className="h-3.5 w-3.5" />{c.label}</div>
            <div className={`text-xl font-head font-bold mt-1 ${c.cls}`}>{c.val}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Ref", "Contract", "Site", "Type", "Revenue", "Cost", "Margin", "Margin %", "Pipeline", ""].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap uppercase text-xs tracking-wide">{h}</th>)}</tr></thead>
          <tbody>
            {data === null && <tr><td colSpan={10} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {data && data.contracts.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-400">No contracts yet. Create one to start tracking margin.</td></tr>}
            {data && data.contracts.map((c) => (
              <tr key={c.id} data-testid={`contract-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(c)}>
                <td className="px-3 py-2.5 font-medium accent-text whitespace-nowrap">{c.ref}</td>
                <td className="px-3 py-2.5 text-slate-800">{c.name}
                  {(c.template_ref || c.project_ref) && (
                    <Link to={c.template_ref ? `/app/job-templates?open=${c.template_id}` : `/app/projects?open=${c.project_id}`}
                      onClick={(e) => e.stopPropagation()} data-testid={`contract-linked-${c.id}`}
                      className="flex items-center gap-1 text-[11px] accent-text hover:underline mt-0.5 w-fit">
                      {c.template_ref ? <Repeat className="h-3 w-3" /> : <FolderKanban className="h-3 w-3" />}
                      {c.template_ref ? `Recurring Work ${c.template_ref}` : `Project ${c.project_ref}`}{c.job_ref ? ` · ${c.job_ref}` : ""}
                    </Link>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-500">{c.site_name || "—"}</td>
                <td className="px-3 py-2.5"><StatusBadge status={c.contract_type || "—"} /></td>
                <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{money(c.revenue, cur)}</td>
                <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{money(c.cost, cur)}</td>
                <td className={`px-3 py-2.5 font-semibold whitespace-nowrap ${marginColor(c.margin_pct)}`}>{money(c.margin, cur)}</td>
                <td className={`px-3 py-2.5 font-semibold ${marginColor(c.margin_pct)}`}>{c.margin_pct}%</td>
                <td className="px-3 py-2.5 accent-text whitespace-nowrap">{money(c.pipeline_value, cur)}</td>
                <td className="px-3 py-2.5 text-right"><Button variant="ghost" size="sm" data-testid={`contract-edit-${c.id}`} onClick={(e) => { e.stopPropagation(); openEdit(c); }}>Edit</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? `Edit ${editing.ref}` : "New Contract"}</DialogTitle></DialogHeader>
          <RecordForm fields={MODULES.contracts.fields} value={form} onChange={setForm} />
          <p className="text-xs text-slate-400">Recurring contracts auto-generate visits 3 months ahead into the Unscheduled pool — the contract value is spread across the year's visits. One-off contracts create a Project plus a job ready to schedule.</p>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-accent" data-testid="contract-save-btn" onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save Contract</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="margin-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-head">{detail.contract.ref} · {detail.contract.name}</DialogTitle></DialogHeader>
              {loadingDetail || !detail.breakdown ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div> : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[["Revenue", money(detail.revenue, cur)], ["Cost", money(detail.cost, cur)], ["Margin", money(detail.margin, cur)], ["Margin %", `${detail.margin_pct}%`]].map(([k, v], i) => (
                      <div key={k} className="bg-slate-50 rounded-md p-3"><div className="text-xs text-slate-400">{k}</div><div className={`text-lg font-head font-bold ${i >= 2 ? marginColor(detail.margin_pct) : "text-slate-900"}`}>{v}</div></div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-head font-semibold text-sm mb-2">Revenue</h4>
                      <Row label="Contract value" val={money(detail.breakdown.contract_value, cur)} />
                      <Row label="Accepted quotes" val={money(detail.breakdown.accepted_quotes_value, cur)} />
                    </div>
                    <div>
                      <h4 className="font-head font-semibold text-sm mb-2">Cost</h4>
                      <Row label={`Labour (${detail.breakdown.labour_hours}h × ${money(detail.breakdown.labour_rate, cur)})`} val={money(detail.breakdown.labour_cost, cur)} />
                      <Row label="Materials" val={money(detail.breakdown.materials_cost, cur)} />
                      <Row label="Projects" val={money(detail.breakdown.project_cost, cur)} />
                    </div>
                  </div>
                  <div className="border-t border-slate-100 pt-3" data-testid="margin-trend">
                    <h4 className="font-head font-semibold text-sm mb-2">Margin trend · last 6 months</h4>
                    {trend === null ? <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline text-slate-400" /></div> :
                      trend.length === 0 ? <p className="text-xs text-slate-400">No trend data.</p> : (
                        <div className="h-[190px] -ml-3" style={{ minHeight: 190 }}>
                          <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                            <LineChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={55} />
                              <Tooltip formatter={(v, n) => [money(v, cur), n]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#0284C7" strokeWidth={2} dot={{ r: 2.5 }} />
                              <Line type="monotone" dataKey="cost" name="Cost" stroke="#DC2626" strokeWidth={2} dot={{ r: 2.5 }} />
                              <Line type="monotone" dataKey="margin" name="Margin" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    <p className="text-[10px] text-slate-400 mt-1">Recurring contract value amortised at 1/12 per month · costs from completed jobs in each month.</p>
                  </div>
                  <div className="border-t border-slate-100 pt-3">
                    <h4 className="font-head font-semibold text-sm mb-2 flex items-center justify-between">Pipeline · unaccepted quotes<span className="accent-text">{money(detail.pipeline_value, cur)}</span></h4>
                    {detail.breakdown.pipeline_quotes.length === 0 ? <p className="text-xs text-slate-400">No open quotes for this site.</p> : (
                      <div className="space-y-1">
                        {detail.breakdown.pipeline_quotes.map((q) => (
                          <div key={q.ref} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1.5">
                            <span className="flex items-center gap-2"><span className="accent-text font-medium">{q.ref}</span><StatusBadge status={q.status} /></span>
                            <span className="text-slate-700 font-medium">{money(q.selling_price || 0, cur)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
